import React, { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { compPlanStatementSelector } from "src/redux/selectors/custom-selector";
import useCompPlanStatementService from "./service";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import CustomDropdown from "../../components/customDropdown";
import CustomMultiSelect from "../../components/multiSelect";
import {
  setIsProviderDetailFlagAction,
  setPrefetchDropdownAction,
  setSelectedCompPlanAction,
  setSelectedGroupsAction,
  setSelectedPeriodAction,
  setSelectedProviderAction,
  setSelectedSpecialitiesAction,
} from "src/redux/actions/compplan-actions";

import { Switch } from "@progress/kendo-react-inputs";
import "./plan-report.scss";

const PlanCompFilter = ({ runReports }) => {
  const {
    compPlanData,
    isCompPlanLoading,
    selectedCompPlan,
    compPlanPeriodData,
    isCompPlanPeriodLoading,
    selectedCompPlanPeriod,

    specialityData,
    isSpecialityLoading,
    selectedSpecialities,

    groupData,
    isGroupLoading,
    selectedGroups,

    providerData,
    isProviderLoading,
    selectedProviders,
    prefetchDropdowns,
    isProviderDetail,
  } = useSelector(compPlanStatementSelector, shallowEqual);
  const dispatch = useDispatch();
  const filteredSpecialityData = React.useMemo(
    () => specialityData.filter((it) => it.specialty_key !== 0),
    [specialityData]
  );
  const filteredGroupData = React.useMemo(() => groupData.filter((it) => it.org_title_key !== 0), [groupData]);
  const filteredProviderData = React.useMemo(
    () => providerData.filter((it) => it.provider_key !== 0),
    [providerData]
  );

  const isCompPlanOpenRef = React.useRef(false);
  const isCompPlanPeriodOpenRef = React.useRef(false);
  const isSpecialityOpenRef = React.useRef(false);
  const isGroupOpenRef = React.useRef(false);
  const isProviderOpenRef = React.useRef(false);

  const [activeCompPlanList, setActiveCompPlanList] = React.useState(compPlanData || []);
  const [activeCompPlanPeriodList, setActiveCompPlanPeriodList] = React.useState(compPlanPeriodData || []);
  const [activeSpecialityList, setActiveSpecialityList] = React.useState(filteredSpecialityData || []);
  const [activeGroupList, setActiveGroupList] = React.useState(filteredGroupData || []);
  const [activeProviderList, setActiveProviderList] = React.useState(filteredProviderData || []);

  const reportService = useCompPlanStatementService();

  React.useEffect(() => {
    if (compPlanData?.length > 0) {
      if (!isCompPlanOpenRef.current) {
        setActiveCompPlanList(compPlanData);
      }
    } else {
      reportService.getCompPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanData]);

  React.useEffect(() => {
    if (!isCompPlanPeriodOpenRef.current) {
      setActiveCompPlanPeriodList(compPlanPeriodData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compPlanPeriodData]);

  React.useEffect(() => {
    if (!isSpecialityOpenRef.current) {
      setActiveSpecialityList(filteredSpecialityData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSpecialityData]);

  React.useEffect(() => {
    if (!isGroupOpenRef.current) {
      setActiveGroupList(filteredGroupData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGroupData]);

  React.useEffect(() => {
    if (!isProviderOpenRef.current) {
      setActiveProviderList(filteredProviderData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProviderData]);

  const isManualFilterClearEvent = (event) => {
    const filterText = event?.filter?.value;
    const eventType = event?.syntheticEvent?.type;
    const inputType = event?.syntheticEvent?.nativeEvent?.inputType;

    return (
      filterText === "" &&
      (eventType === "input" ||
        inputType === "deleteContentBackward" ||
        inputType === "deleteContentForward" ||
        inputType === "deleteByCut")
    );
  };

  const getStableFilteredList = (prevData, nextData, fullData, isOpenRef, event) => {
    if (isManualFilterClearEvent(event)) {
      return nextData;
    }

    const shouldKeepExistingFilteredData =
      isOpenRef.current &&
      Array.isArray(prevData) &&
      Array.isArray(nextData) &&
      Array.isArray(fullData) &&
      prevData.length < fullData.length &&
      nextData.length === fullData.length;

    return shouldKeepExistingFilteredData ? prevData : nextData;
  };

  useEffect(() => {
    if (selectedCompPlan && prefetchDropdowns) {
      reportService.fetchCompPlanPeriods(selectedCompPlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompPlan, prefetchDropdowns]);

  const lastFetchedSpecialitiesRef = React.useRef(null);
  useEffect(() => {
    const compPlanKey = selectedCompPlan?.comp_plan_mstr_key || 0;
    const periodKey = selectedCompPlanPeriod?.period_mstr_key || 0;
    const compositeKey = [compPlanKey, periodKey].join("|");
    if (prefetchDropdowns && selectedCompPlanPeriod && lastFetchedSpecialitiesRef.current !== compositeKey) {
      reportService.fetchSpecialities(selectedCompPlanPeriod);
      lastFetchedSpecialitiesRef.current = compositeKey;
    } else {
      lastFetchedSpecialitiesRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompPlanPeriod, prefetchDropdowns]);

  const lastFetchedGroupRef = React.useRef(null);
  useEffect(() => {
    if (isCompPlanPeriodLoading || isSpecialityLoading) {
      return;
    }
    const compPlanKey = selectedCompPlan?.comp_plan_mstr_key || 0;
    const periodKey = selectedCompPlanPeriod?.period_mstr_key || 0;
    const specialityKeys = (selectedSpecialities || [])
      .map((g) => g.specialty_key)
      .sort()
      .join(",");
    const compositeKey = [compPlanKey, periodKey, specialityKeys].join("|");
    if (
      prefetchDropdowns &&
      selectedCompPlanPeriod &&
      selectedSpecialities &&
      lastFetchedGroupRef.current !== compositeKey
    ) {
      reportService.fetchGroups(selectedSpecialities);
      lastFetchedGroupRef.current = compositeKey;
    } else {
      lastFetchedGroupRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecialities, selectedCompPlanPeriod, prefetchDropdowns, isCompPlanPeriodLoading, isSpecialityLoading]);

  const lastFetchedProviderRef = React.useRef(null);
  useEffect(() => {
    if (isCompPlanPeriodLoading || isSpecialityLoading || isGroupLoading) {
      return;
    }
    const compPlanKey = selectedCompPlan?.comp_plan_mstr_key || 0;
    const periodKey = selectedCompPlanPeriod?.period_mstr_key || 0;
    const specialityKeys = (selectedSpecialities || [])
      .map((g) => g.specialty_key)
      .sort()
      .join(",");
    const groupKeys = (selectedGroups || [])
      .map((g) => g.org_title_key)
      .sort()
      .join(",");
    const compositeKey = [compPlanKey, periodKey, specialityKeys, groupKeys].join("|");
    if (
      prefetchDropdowns &&
      selectedGroups &&
      selectedCompPlanPeriod &&
      selectedSpecialities &&
      lastFetchedProviderRef.current !== compositeKey
    ) {
      reportService.fetchProviders(selectedGroups);
      lastFetchedProviderRef.current = compositeKey;
    } else {
      lastFetchedProviderRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups, prefetchDropdowns, isCompPlanPeriodLoading, isSpecialityLoading, isGroupLoading]);

  const handleChangeCompPlan = (value) => {
    dispatch(setPrefetchDropdownAction(true));
    dispatch(setSelectedCompPlanAction(value));
  };

  const handleChangeCompPlanPeriod = (value) => {
    dispatch(setPrefetchDropdownAction(true));
    dispatch(setSelectedPeriodAction(value));
  };

  const handleChangeSpecialities = (value) => {
    dispatch(setPrefetchDropdownAction(true));
    const items =
      value.length === 0 || value.findIndex((it) => it.specialty_key === 0) > 0
        ? [specialityData[0]]
        : value.filter((it) => it.specialty_key !== 0);
    dispatch(setSelectedSpecialitiesAction(items));
  };

  const handleChangeGroups = (value) => {
    dispatch(setPrefetchDropdownAction(true));
    const items =
      value.length === 0 || value.findIndex((it) => it.org_title_key === 0) > 0
        ? [groupData[0]]
        : value.filter((it) => it.org_title_key !== 0);
    dispatch(setSelectedGroupsAction(items));
  };

  const handleChangeProvider = (value) => {
    const items =
      value.length === 0 || value.findIndex((it) => it.provider_key === 0) > 0
        ? [providerData[0]]
        : value.filter((it) => it.provider_key !== 0);
    dispatch(setSelectedProviderAction(items));
  };

  return (
    <ul className="ReportFilters-links reports-filters">
      <div className={"k-form-field-wrap-popup valueDropdown width280"} style={{ paddingRight: "5px !important" }}>
        <Label>{"Comp Plan:"}</Label>
        <CustomDropdown
          name="compPlanDropdown"
          data={activeCompPlanList}
          textField="comp_plan_name"
          dataItemKey="comp_plan_mstr_key"
          loading={isCompPlanLoading}
          valueData={selectedCompPlan}
          onChange={(value) => handleChangeCompPlan(value)}
          filterable={true}
          dataList={compPlanData}
          preserveFilteredDataOnSelect={true}
          passFilterEvent={true}
          onOpen={() => {
            isCompPlanOpenRef.current = true;
          }}
          onClose={() => {
            isCompPlanOpenRef.current = false;
            setActiveCompPlanList(compPlanData);
          }}
          onFilterChange={(dataValue, event) =>
            setActiveCompPlanList((prevData) =>
              getStableFilteredList(prevData, dataValue, compPlanData, isCompPlanOpenRef, event)
            )
          }
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown width125"} style={{ paddingRight: "5px !important" }}>
        <Label>{"Comp Plan Period:"}</Label>
        <CustomDropdown
          name="compPlanPeriodDropdown"
          data={activeCompPlanPeriodList}
          textField="period_name"
          dataItemKey="period_mstr_key"
          loading={isCompPlanPeriodLoading}
          valueData={selectedCompPlanPeriod}
          onChange={(value) => handleChangeCompPlanPeriod(value)}
          filterable={true}
          dataList={compPlanPeriodData}
          preserveFilteredDataOnSelect={true}
          passFilterEvent={true}
          onOpen={() => {
            isCompPlanPeriodOpenRef.current = true;
          }}
          onClose={() => {
            isCompPlanPeriodOpenRef.current = false;
            setActiveCompPlanPeriodList(compPlanPeriodData);
          }}
          onFilterChange={(dataValue, event) =>
            setActiveCompPlanPeriodList((prevData) =>
              getStableFilteredList(prevData, dataValue, compPlanPeriodData, isCompPlanPeriodOpenRef, event)
            )
          }
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown"} style={{ paddingRight: "5px !important" }}>
        <Label>{"Specialty:"}</Label>
        <CustomMultiSelect
          name="specialityDropdown"
          data={activeSpecialityList}
          textField="specialty_name"
          dataItemKey="specialty_key"
          loading={isSpecialityLoading}
          valueData={selectedSpecialities}
          onChange={(value) => handleChangeSpecialities(value)}
          filterable={true}
          isSelectALl={false}
          dataList={filteredSpecialityData}
          preserveFilteredDataOnSelect={true}
          passFilterEvent={true}
          onOpen={() => {
            isSpecialityOpenRef.current = true;
          }}
          onClose={() => {
            isSpecialityOpenRef.current = false;
            setActiveSpecialityList(filteredSpecialityData);
          }}
          onFilterChange={(dataValue, event) =>
            setActiveSpecialityList((prevData) =>
              getStableFilteredList(prevData, dataValue, filteredSpecialityData, isSpecialityOpenRef, event)
            )
          }
          style={{ maxHeight: 200 }}
          autoClose={false}
          // disabled={isAdvanceFilterApplied}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown"} style={{ paddingRight: "5px !important" }}>
        <Label>{"Group:"}</Label>
        <CustomMultiSelect
          name="groupDropdown"
          data={activeGroupList}
          textField="title_name"
          dataItemKey="org_title_key"
          loading={isGroupLoading}
          valueData={selectedGroups}
          onChange={(value) => handleChangeGroups(value)}
          filterable={true}
          isSelectALl={false}
          dataList={filteredGroupData}
          preserveFilteredDataOnSelect={true}
          passFilterEvent={true}
          onOpen={() => {
            isGroupOpenRef.current = true;
          }}
          onClose={() => {
            isGroupOpenRef.current = false;
            setActiveGroupList(filteredGroupData);
          }}
          onFilterChange={(dataValue, event) =>
            setActiveGroupList((prevData) =>
              getStableFilteredList(prevData, dataValue, filteredGroupData, isGroupOpenRef, event)
            )
          }
          style={{ maxHeight: 200 }}
          autoClose={false}
          // disabled={isAdvanceFilterApplied}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown"} style={{ paddingRight: "5px !important" }}>
        <Label>{"Provider:"}</Label>
        <CustomMultiSelect
          name="providerDropdown"
          data={activeProviderList}
          textField="provider_name"
          dataItemKey="provider_key"
          loading={isProviderLoading}
          valueData={selectedProviders}
          onChange={(value) => handleChangeProvider(value)}
          filterable={true}
          isSelectALl={false}
          dataList={filteredProviderData}
          preserveFilteredDataOnSelect={true}
          passFilterEvent={true}
          onOpen={() => {
            isProviderOpenRef.current = true;
          }}
          onClose={() => {
            isProviderOpenRef.current = false;
            setActiveProviderList(filteredProviderData);
          }}
          onFilterChange={(dataValue, event) =>
            setActiveProviderList((prevData) =>
              getStableFilteredList(prevData, dataValue, filteredProviderData, isProviderOpenRef, event)
            )
          }
          style={{ maxHeight: 200 }}
          autoClose={false}
          // disabled={isAdvanceFilterApplied}
        />
      </div>
      <div className={"k-form-field-wrap-popup valueDropdown"} style={{ width: "125px" }}>
        <Label>{"Provider Detail:"}</Label>
        <Switch
          className="result-switch"
          onChange={(e) => {
            dispatch(setIsProviderDetailFlagAction(e.value));
          }}
          thumbRounded={"medium"}
          trackRounded={"medium"}
          size={"medium"}
          checked={isProviderDetail}
          onLabel={"ON"}
          offLabel={"OFF"}
        />
      </div>
      <div className="k-form-buttons btn-comp-plan-filter">
        <Button
          primary={true}
          disabled={
            !selectedCompPlan ||
            !selectedCompPlanPeriod ||
            !selectedSpecialities.length ||
            !selectedGroups.length ||
            !selectedProviders.length ||
            isCompPlanLoading ||
            isCompPlanPeriodLoading ||
            isSpecialityLoading ||
            isGroupLoading ||
            isProviderLoading
          }
          type={"submit"}
          onClick={() => {
            runReports();
          }}
          className="primarybtn"
        >
          Run
        </Button>
      </div>
    </ul>
  );
};
export default PlanCompFilter;
